import getCurrentEnv from "./getCurrentEnv";

const FORCE_KEY = "forceUserType";

/**
 * Test-only escape hatch for the IT environment.
 *
 * On the IT host the mechanism can be forced with either:
 *   - a URL query param: `?userType=external` (or `internal`), or
 *   - a sticky sessionStorage flag `forceUserType` (set once from the query
 *     param so it survives the login redirects).
 *
 * This exists because the IT environment only exposes a `us.bank-dns.com`
 * host, which would otherwise always resolve to the internal/SSO path and make
 * the external/CIAM flow untestable there.
 *
 * Returns "internal" | "external" | null. Always null outside the IT env.
 */
const getForcedUserType = () => {
  if (getCurrentEnv() !== "it") return null;

  const fromQuery = new URLSearchParams(window.location.search)
    .get("userType")
    ?.toLowerCase();

  if (fromQuery === "internal" || fromQuery === "external") {
    try {
      sessionStorage.setItem(FORCE_KEY, fromQuery);
    } catch {
      // sessionStorage may be unavailable; fall through to the query value.
    }
    return fromQuery;
  }

  let stored = null;
  try {
    stored = sessionStorage.getItem(FORCE_KEY);
  } catch {
    stored = null;
  }
  return stored === "internal" || stored === "external" ? stored : null;
};

/**
 * Determines whether the current user is "internal" based on the hostname of
 * the URL the application was launched from. Internal (corporate network /
 * SSO-eligible) hosts contain "us.bank-dns.com" anywhere in the hostname.
 * Matching is case-insensitive.
 *
 * In the IT environment this can be overridden via `?userType=external`
 * (or `internal`) — see {@link getForcedUserType}. All other environments are
 * strictly host-based.
 *
 * @returns {boolean} true when the host indicates an internal user (SSO),
 * false otherwise (external / CIAM).
 */
const isInternalUser = () => {
  const forced = getForcedUserType();
  if (forced) return forced === "internal";

  const { hostname } = window.location;
  return hostname.toLowerCase().includes("us.bank-dns.com");
};

export default isInternalUser;
export { getForcedUserType };

